'use client';

import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';
import { useRouter } from 'next/navigation';
import { debounce } from 'lodash-es';
import { Search } from 'lucide-react';
import { fuseSearch } from '@/api/searchIndex';
import { ChoiceBadge } from '@/components/common/ChoiceBadge/ChoiceBadge';
import { getLocale } from '@/i18n/locale';
import { ELocale } from '@/i18n/localeEnum';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { Input } from '@/components/ui/input';
import SearchResultsPopUp from '@/components/common/RateResortSearch/SearchResultsPopUp';
import { Routes } from '@/routes';
import { REVIEW_ANCHOR } from '@/page-components/resort/constants';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { useSanitizedHtmlProps } from '@/util/sanitized';
import { SectionCard } from '@/components/ui/section-card';

interface IRateResortSearchProps {
  regionTitle: string;
  isWidget?: boolean;
}

// Small wrapper around TextInput so the legacy SCSS-styled input gets
// a v3 chrome with the search icon prefix + visible-emphasis treatment.
// Taller, thicker border, darker placeholder so the search is the
// clear primary action of the card (not a faded form field).
const SEARCH_INPUT_OVERRIDES = [
  '[&_input]:!h-12',
  '[&_input]:!w-full',
  '[&_input]:!rounded-md',
  '[&_input]:!border-2',
  '[&_input]:!border-foreground/15',
  '[&_input]:!bg-white',
  '[&_input]:!pl-10',
  '[&_input]:!pr-3',
  '[&_input]:!text-base',
  '[&_input]:!font-medium',
  '[&_input]:!text-foreground',
  '[&_input]:!shadow-sm',
  '[&_input]:placeholder:!text-foreground/55',
  '[&_input]:placeholder:!font-normal',
  '[&_input]:focus-visible:!border-primary/40',
  '[&_input]:focus-visible:!ring-2',
  '[&_input]:focus-visible:!ring-primary/20',
].join(' ');

const SearchField = ({
  onChange, placeholder,
}: {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
}) => (
  <div className="relative">
    <Search
      size={18}
      className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 fill-none text-foreground/55"
    />
    <div className={SEARCH_INPUT_OVERRIDES}>
      <Input onChange={onChange} placeholder={placeholder} />
    </div>
  </div>
);

const RateResortSearch = ({ regionTitle, isWidget = false }: IRateResortSearchProps) => {
  const router = useRouter();
  const locale = getLocale() as ELocale;
  const { t } = useOTSTranslation('common');
  const [searchResults, setSearchResults] = useState<any>(undefined);
  const [isPopupVisible, setIsPopupVisible] = useState<boolean>(!!searchResults);
  const searchInputBlockRef = useRef<HTMLDivElement>(null);
  const hash = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';

  const onChange = useCallback(debounce(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const { results } = await fuseSearch(value, 35, t);

    if (!value && results.resorts.length === 0) {
      setIsPopupVisible(false);
      setSearchResults(undefined);
      return;
    }

    setSearchResults(results.resorts);
    setIsPopupVisible(true);
  }, 300), []);

  const onItemSelect = useCallback((item: any) => {
    if (isWidget && window) {
      window.open(`${Routes.resortTripPlanningReviews(item.resort)}#${REVIEW_ANCHOR}`, '_blank');
    } else {
      router.push(`${Routes.resortTripPlanningReviews(item.resort)}#${REVIEW_ANCHOR}`);
    }
  }, []);

  const handlePopUpStateBasedOnEventParent = useCallback((event: MouseEvent) => {
    if (event.target && event.target instanceof Node) {
      const isClickedInsideSearch = searchInputBlockRef.current && searchInputBlockRef.current.contains(event.target);

      if (!isClickedInsideSearch && isPopupVisible) {
        setIsPopupVisible(false);
      }

      if (isClickedInsideSearch && searchResults) {
        setIsPopupVisible(true);
      }
    }
  }, [searchResults, isPopupVisible]);

  useEffect(() => {
    window.addEventListener('click', handlePopUpStateBasedOnEventParent);

    return () => {
      window.removeEventListener('click', handlePopUpStateBasedOnEventParent);
    };
  }, [searchResults, handlePopUpStateBasedOnEventParent]);

  useEffect(() => {
    const onHashChanged = () => {
      searchInputBlockRef.current?.scrollIntoView();
    };

    window.addEventListener('hashchange', onHashChanged);
    return () => {
      window.removeEventListener('hashchange', onHashChanged);
    };
  }, []);

  useEffect(() => {
    if (hash && hash === REVIEW_ANCHOR) {
      setTimeout(() => {
        searchInputBlockRef.current?.scrollIntoView();
      }, 500);
    }
  }, [hash]);

  const resortReviewHelptext = useSanitizedHtmlProps(t('guide.home.resortReviewHelptext'));

  // Widget mode (embedded in /widget/rate-resort) drops the heading +
  // helper copy and renders just the search field. Standalone mode
  // on /ski-resorts renders the bordered card chrome matching the
  // BrowseByGroup cards below.

  if (isWidget) {
    return (
      <div ref={searchInputBlockRef} className="relative max-w-md">
        <SearchField onChange={onChange} placeholder={t('common.searchResort')} />
        {isPopupVisible && (
          <SearchResultsPopUp
            label="resorts"
            dataKey="title"
            data={searchResults}
            onSelect={onItemSelect}
          />
        )}
      </div>
    );
  }

  return (
    <SectionCard
      id={REVIEW_ANCHOR}
      ref={searchInputBlockRef}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-3">
          <Heading
            level={2}
          >
            {t('guide.home.rateResortLabel', { region: regionTitle })}
          </Heading>
          <Text
            variant="default"
            className="max-w-2xl text-sm leading-relaxed text-muted-foreground [&_a]:font-semibold [&_a]:text-primary [&_a]:hover:underline"
            {...resortReviewHelptext}
          />
          <div className="relative max-w-md pt-1">
            <SearchField onChange={onChange} placeholder={t('common.searchResort')} />
            {isPopupVisible && (
              <SearchResultsPopUp
                label="resorts"
                dataKey="title"
                data={searchResults}
                onSelect={onItemSelect}
              />
            )}
          </div>
        </div>
        <ChoiceBadge
          locale={locale}
          className="hidden h-16 w-auto shrink-0 sm:block md:h-20"
        />
      </div>
    </SectionCard>
  );
};

export default RateResortSearch;
